import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { config } from "@/config";
import { SettingsApi } from "@/api/giftcard.api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const CreateBrand: React.FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [category, setCategory] = useState("");
  const [website, setWebsite] = useState("");
  const [mallId, setMallId] = useState("");
  const [categories, setCategories] = useState<string[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [malls, setMalls] = useState<
    Array<{ mallId: number; mallName: string }>
  >([]);
  const [isLoadingMalls, setIsLoadingMalls] = useState(false);
  const handleLicenseNumberChange = (value: string) => {
    const sanitized = value.replace(/[^a-zA-Z0-9]/g, "");
    setLicenseNumber(sanitized);
  };

  const handlePhoneNumberChange = (value: string) => {
    // Only allow numeric characters
    const sanitized = value.replace(/\D/g, "");
    // Limit to 15 digits
    const limited = sanitized.slice(0, 15);
    setPhoneNumber(limited);
  };

  const fetchMalls = async () => {
    setIsLoadingMalls(true);
    try {
      const data = await SettingsApi.getMallDetails();
      const mallsData = Array.isArray(data?.malldetails)
        ? data.malldetails
        : [];

      const mappedMalls = mallsData.map((mall: any) => ({
        mallId: mall.mallId,
        mallName: mall.mallName || `Mall ${mall.mallId}`,
      }));

      setMalls(mappedMalls);
    } catch (error) {
      console.error("Error fetching malls:", error);
      setMalls([]);
      toast.error("Failed to load malls");
    } finally {
      setIsLoadingMalls(false);
    }
  };

  const fetchCategories = async (mallIdParam: number) => {
    setIsLoadingCategories(true);
    try {
      const userInfoRaw = sessionStorage.getItem("userInfo");
      const userId = userInfoRaw ? JSON.parse(userInfoRaw)?.userId ?? 0 : 0;

      const response = await fetch(
        `${config.GIFT_API_BASE_URL}/Settings/GetSettings`,
        {
          method: "POST",
          headers: {
            xapikey: config.GIFT_API_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ p_Id: userId, p_MallId: mallIdParam }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch settings");
      }

      const data = await response.json();
      const categoryList = data?.setting?.categoryList;

      if (categoryList) {
        const parsed = categoryList
          .split(",")
          .map((item: string) => item.trim())
          .filter((item: string) => item.length > 0);
        setCategories(parsed);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
      toast.error("Failed to load categories");
    } finally {
      setIsLoadingCategories(false);
    }
  };

  useEffect(() => {
    fetchMalls();
  }, []);

  useEffect(() => {
    if (mallId) {
      const mallIdNum = parseInt(mallId);
      if (!isNaN(mallIdNum)) {
        fetchCategories(mallIdNum);
        setCategory(""); // Reset category when mall changes
      }
    } else {
      setCategories([]);
      setCategory("");
    }
  }, [mallId]);

  const handleCancel = () => {
    navigate("/giftcard/brands");
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!name.trim()) return toast.error("Name is required");
    if (!licenseNumber.trim()) return toast.error("License Number is required");
    if (!phoneNumber.trim()) return toast.error("Phone Number is required");
    if (!email.trim()) return toast.error("Email is required");
    if (!address.trim()) return toast.error("Address is required");
    if (!category) return toast.error("Please select a category");
    if (!mallId) return toast.error("Please select a mall");

    // Phone number validation
    const phoneDigits = phoneNumber.replace(/\D/g, "");
    if (phoneDigits.length < 7) {
      return toast.error("Phone number must be at least 7 digits");
    }
    if (phoneDigits.length > 15) {
      return toast.error("Phone number must not exceed 15 digits");
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return toast.error("Please enter a valid email address");
    }

    setIsSubmitting(true);
    try {
      // Get userId and username from sessionStorage
      const userInfo = sessionStorage.getItem("userInfo");
      const parsedUserInfo = userInfo ? JSON.parse(userInfo) : null;
      const userId = parsedUserInfo?.userId || 0;
      const userName = parsedUserInfo?.username || "";

      // Prepare API payload
      const payload = {
        businessName: name,
        traderlicenseNumber: licenseNumber,
        phoneNo: phoneNumber,
        email: email,
        address: address,
        category: category,
        webSite: website,
        MallId: mallId ? parseInt(mallId) : null,
        isActive: true,
        createdBy: userId,
        userName: userName,
      };

      console.log("Creating merchant:", payload);

      // Call API
      const response = await axios.post(
        `${config.GIFT_API_BASE_URL}/Merchant/CreateUpdateMerchant`,
        payload,
        {
          headers: {
            accept: "text/plain",
            "Content-Type": "application/json",
            XApiKey: config.GIFT_API_KEY,
          },
        }
      );

      const data = response.data;

      // If API returns an error message, show it and stay on the same page
      if (data && typeof data.error_msg === "string" && data.error_msg.trim()) {
        toast.error(data.error_msg);
        return;
      }

      console.log("Brand created successfully:", data);
      toast.success("Brand created successfully!");
      navigate("/giftcard/brands");
    } catch (error: any) {
      console.error("Error creating merchant:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create merchant";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          className="p-2"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Create New Brand</h1>
          <p className="text-gray-600 mt-1">
            Add a new brand to accept gift cards
          </p>
        </div>
      </div>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle>Brand Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-6" onSubmit={onSubmit}>
            {/* Mall */}
            <div className="space-y-2">
              <Label htmlFor="mallId">Mall *</Label>
              <Select
                value={mallId}
                onValueChange={setMallId}
                disabled={isLoadingMalls}
              >
                <SelectTrigger id="mallId">
                  <SelectValue
                    placeholder={
                      isLoadingMalls ? "Loading malls..." : "Select mall"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {malls.length > 0 ? (
                    malls.map((mall) => (
                      <SelectItem
                        key={mall.mallId}
                        value={mall.mallId.toString()}
                      >
                        {mall.mallName}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      {isLoadingMalls ? "Loading..." : "No malls found"}
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter brand name"
              />
            </div>

            {/* License Number */}
            <div className="space-y-2">
              <Label htmlFor="licenseNumber">License Number *</Label>
              <Input
                id="licenseNumber"
                value={licenseNumber}
                onChange={(e) => handleLicenseNumberChange(e.target.value)}
                placeholder="Enter license number"
              />
            </div>

            {/* Phone Number */}
            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Phone Number *</Label>
              <Input
                id="phoneNumber"
                type="tel"
                inputMode="numeric"
                value={phoneNumber}
                onChange={(e) => handlePhoneNumberChange(e.target.value)}
                placeholder="Enter phone number (7-15 digits)"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="brand@example.com"
              />
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="address">Address *</Label>
              <Textarea
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                rows={3}
                placeholder="Enter brand address"
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select
                value={category}
                onValueChange={setCategory}
                disabled={isLoadingCategories || !mallId}
              >
                <SelectTrigger id="category">
                  <SelectValue
                    placeholder={
                      !mallId
                        ? "Please select a mall first"
                        : isLoadingCategories
                        ? "Loading categories..."
                        : "Select category"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {categories.length > 0 ? (
                    categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      {isLoadingCategories
                        ? "Loading..."
                        : "No categories found"}
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Website */}
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                type="url"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://example.com"
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-center gap-4 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isSubmitting}
                className="px-8"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-amber-600 hover:bg-amber-700 text-white px-8"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Creating..." : "Submit"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateBrand;
